'use client';

/**
 * BEAUTIFUL DATE TIME PICKER COMPONENT
 * 
 * A styled date/time picker with:
 * - Visual calendar dropdown
 * - Time selection
 * - Quick date buttons (Today, Tomorrow, Next Week)
 * - Clear and intuitive display
 * 
 * @version 1.0.0
 * @date December 2025
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
  Check
} from 'lucide-react';
import { formatDateSAST, formatTimeSAST, getTimePartsSAST, getDateStrSAST } from '@/lib/sastTime';

// ============================================================================
// TYPES
// ============================================================================

interface DateTimePickerProps {
  value: Date | string | null | undefined;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  showTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  isDark?: boolean;
  className?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const formatDate = (date: Date): string => {
  return formatDateSAST(date, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatTime = (date: Date): string => {
  return formatTimeSAST(date);
};

// Export this helper for use in other components
// Handles both Date objects and ISO strings
export const formatForInput = (dateInput: Date | string): string => {
  // If it's already a string in the right format, return it
  if (typeof dateInput === 'string') {
    // If it already looks like an input format, return as-is
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateInput)) {
      return dateInput.slice(0, 16);
    }
    // Otherwise parse it as a date
    dateInput = new Date(dateInput);
  }

  // Validate it's a valid date
  if (!(dateInput instanceof Date) || isNaN(dateInput.getTime())) {
    return '';
  }

  const dateStr = getDateStrSAST(dateInput);
  const timeParts = getTimePartsSAST(dateInput);
  const hours = String(timeParts.hours).padStart(2, '0');
  const minutes = String(timeParts.minutes).padStart(2, '0');
  return `${dateStr}T${hours}:${minutes}`;
};

const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DateTimePicker({
  value,
  onChange,
  label,
  placeholder = 'Select date & time...',
  showTime = true,
  minDate,
  maxDate,
  disabled = false,
  isDark = false,
  className = ''
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      return new Date(value);
    }
    return new Date();
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (value) {
      return new Date(value);
    }
    return null;
  });

  const [tempHour, setTempHour] = useState(() => {
    if (value) {
      const timeParts = getTimePartsSAST(new Date(value));
      return timeParts.hours;
    }
    return 9; // Default 9 AM
  });

  const [tempMinute, setTempMinute] = useState(() => {
    if (value) {
      const timeParts = getTimePartsSAST(new Date(value));
      return timeParts.minutes;
    }
    return 0;
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Update when value changes externally
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      setViewDate(date);
      const timeParts = getTimePartsSAST(date);
      setTempHour(timeParts.hours);
      setTempMinute(timeParts.minutes);
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calendar navigation
  const prevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  // Date selection
  const selectDate = (day: number) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day, tempHour, tempMinute);
    setSelectedDate(newDate);

    if (!showTime) {
      onChange(formatForInput(newDate));
      setIsOpen(false);
    }
  };

  // Apply selection
  const applySelection = () => {
    if (selectedDate) {
      const finalDate = new Date(selectedDate);
      finalDate.setHours(tempHour, tempMinute, 0, 0);
      onChange(formatForInput(finalDate));
    }
    setIsOpen(false);
  };

  // Quick date buttons
  const setToday = () => {
    const today = new Date();
    today.setHours(tempHour, tempMinute, 0, 0);
    setSelectedDate(today);
    setViewDate(today);
  };

  const setTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(tempHour, tempMinute, 0, 0);
    setSelectedDate(tomorrow);
    setViewDate(tomorrow);
  };

  const setNextWeek = () => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    nextWeek.setHours(tempHour, tempMinute, 0, 0);
    setSelectedDate(nextWeek);
    setViewDate(nextWeek);
  };

  const clearSelection = () => {
    setSelectedDate(null);
    onChange('');
    setIsOpen(false);
  };

  // Build calendar grid
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Check if date is disabled
  const isDateDisabled = (day: number): boolean => {
    const date = new Date(year, month, day);
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  // Check if date is today
  const isToday = (day: number): boolean => {
    return year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
  };

  // Check if date is selected
  const isSelected = (day: number): boolean => {
    if (!selectedDate) return false;
    return (
      year === selectedDate.getFullYear() &&
      month === selectedDate.getMonth() &&
      day === selectedDate.getDate()
    );
  };

  // Display value
  const displayValue = selectedDate
    ? `${formatDate(selectedDate)}${showTime ? ` at ${String(tempHour).padStart(2, '0')}:${String(tempMinute).padStart(2, '0')}` : ''}`
    : '';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {label}
        </label>
      )}

      {/* Input Display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 text-left rounded-lg border flex items-center gap-2 transition-colors ${isDark
          ? 'bg-gray-700 border-gray-600 text-gray-200 hover:border-gray-500'
          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''
          }`}
      >
        <Calendar size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
        <span className={`flex-1 text-sm ${!displayValue ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}`}>
          {displayValue || placeholder}
        </span>
        {displayValue && !disabled && (
          <X
            size={16}
            className={`${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} cursor-pointer`}
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
            }}
          />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 mt-2 p-4 rounded-xl shadow-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`} style={{ minWidth: '320px' }}>

          {/* Quick Buttons */}
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={setToday}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={setTomorrow}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Tomorrow
            </button>
            <button
              type="button"
              onClick={setNextWeek}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Next Week
            </button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={prevMonth}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                }`}
            >
              <ChevronLeft size={18} />
            </button>
            <span className={`font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              {MONTHS[month]} {year}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                }`}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(day => (
              <div
                key={day}
                className={`text-center text-xs font-medium py-1 ${isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {calendarDays.map((day, index) => (
              <div key={index} className="aspect-square">
                {day !== null && (
                  <button
                    type="button"
                    onClick={() => !isDateDisabled(day) && selectDate(day)}
                    disabled={isDateDisabled(day)}
                    className={`w-full h-full rounded-lg text-sm font-medium transition-colors ${isSelected(day)
                      ? 'bg-blue-500 text-white'
                      : isToday(day)
                        ? isDark
                          ? 'bg-blue-900/30 text-blue-400 border border-blue-500'
                          : 'bg-blue-50 text-blue-600 border border-blue-300'
                        : isDateDisabled(day)
                          ? isDark
                            ? 'text-gray-600 cursor-not-allowed'
                            : 'text-gray-300 cursor-not-allowed'
                          : isDark
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    {day}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Time Selector */}
          {showTime && (
            <div className={`border-t pt-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <Clock size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Time:</span>
                <div className="flex items-center gap-1">
                  <select
                    value={tempHour}
                    onChange={(e) => setTempHour(parseInt(e.target.value))}
                    className={`px-2 py-1.5 rounded-lg text-sm font-medium border ${isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-200'
                      : 'bg-gray-50 border-gray-300 text-gray-700'
                      }`}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>:</span>
                  <select
                    value={tempMinute}
                    onChange={(e) => setTempMinute(parseInt(e.target.value))}
                    className={`px-2 py-1.5 rounded-lg text-sm font-medium border ${isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-200'
                      : 'bg-gray-50 border-gray-300 text-gray-700'
                      }`}
                  >
                    {[0, 15, 30, 45].map(min => (
                      <option key={min} value={min}>
                        {String(min).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={`flex justify-end gap-2 mt-4 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isDark
                ? 'text-gray-400 hover:bg-gray-700'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applySelection}
              disabled={!selectedDate}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${selectedDate
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : isDark
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
            >
              <Check size={14} />
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
